import { useCallback, useEffect, useState } from "react";
import { Loader2, Wand2, UserCheck, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RESULT_STYLE = {
  READY_TO_ASSIGN: "bg-[#E7F0FF] text-[#0058C7]",
  ALREADY_CORRECT: "bg-[#E9F9EF] text-[#0E8A46]",
  MAPPING_CONFLICT: "bg-[#FDE7EC] text-[#C8102E]",
  DUPLICATE_PIN_CONFLICT: "bg-[#FDE7EC] text-[#C8102E]",
  INVALID_NIP: "bg-[#F1F1F4] text-[#6E6E73]",
  EXCLUDED_PIN: "bg-[#FFF1E6] text-[#B5590F]",
};

const Stat = ({ label, value, tone }) => (
  <div className="rounded-xl bg-white/60 px-3 py-2.5">
    <p className="num text-[20px] font-bold leading-none" style={tone ? { color: tone } : {}}>{value}</p>
    <p className="mt-1 text-[11px] text-[#8E8E93]">{label}</p>
  </div>
);

export default function FingerspotEnrollment({ onChanged }) {
  const { readOnly } = useAuth();
  const [rd, setRd] = useState(null);
  const [busy, setBusy] = useState(null);
  const [assignPv, setAssignPv] = useState(null);
  const [sourcePv, setSourcePv] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/fingerspot/readiness");
      setRd(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshAll = async () => { await load(); onChanged && onChanged(); };

  const openAssign = async () => {
    setBusy("assign-pv");
    try { setAssignPv((await api.get("/fingerspot/auto-assign/preview")).data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  const applyAssign = async () => {
    setBusy("assign-apply");
    try {
      const { data } = await api.post("/fingerspot/auto-assign/apply");
      toast.success(`Auto-assigned ${data.assigned} PIN(s)`);
      setAssignPv(null); await refreshAll();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  const openSource = async () => {
    setBusy("src-pv");
    try { setSourcePv((await api.get("/fingerspot/activate-source/preview")).data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  const applySource = async () => {
    setBusy("src-apply");
    try {
      const { data } = await api.post("/fingerspot/activate-source/apply");
      toast.success(`Activated ${data.changed} employee(s) for Fingerspot`);
      setSourcePv(null); await refreshAll();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  if (!rd) return null;
  const willChange = (sourcePv?.rows || []).filter((r) => r.will_change);

  return (
    <section data-testid="fp-enrollment" className="liquid-glass-card space-y-4 p-5">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight">
          <ShieldCheck className="h-4.5 w-4.5 text-[#0071E3]" /> Enrollment Readiness
        </h2>
        <button data-testid="fp-readiness-refresh" onClick={load} className="flex items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3 py-1.5 text-[12px] font-medium text-[#1D1D1F] transition-all hover:bg-white">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        <Stat label="Active" value={rd.total_active} />
        <Stat label="Mapped" value={rd.mapped} tone="#0E8A46" />
        <Stat label="Auto" value={rd.auto_mapped} />
        <Stat label="Manual" value={rd.manual_mapped} />
        <Stat label="Unmapped" value={rd.unmapped} tone={rd.unmapped ? "#B5590F" : undefined} />
        <Stat label="Conflicts" value={rd.mapping_conflicts} tone={rd.mapping_conflicts ? "#C8102E" : undefined} />
        <Stat label="Fingerspot Payroll" value={rd.fingerspot_payroll} tone="#0058C7" />
        <Stat label="Attendance Only" value={rd.piece_rate ?? 0} tone={rd.piece_rate ? "#B5590F" : undefined} />
        <Stat label="Manual Payroll" value={rd.manual_payroll} />
      </div>

      {!readOnly && (
      <div className="flex flex-wrap gap-2">
        <button data-testid="fp-auto-assign-btn" onClick={openAssign} disabled={busy === "assign-pv"}
          className="flex h-9 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[12.5px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50">
          {busy === "assign-pv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Auto-Assign Fingerspot PINs
        </button>
        <button data-testid="fp-activate-source-btn" onClick={openSource} disabled={busy === "src-pv"}
          className="flex h-9 items-center gap-1.5 rounded-full bg-[#128C3E] px-4 text-[12.5px] font-medium text-white transition-all hover:bg-[#0F7433] active:scale-[0.98] disabled:opacity-50">
          {busy === "src-pv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} Activate Fingerspot for All Ready
        </button>
      </div>
      )}

      {rd.mapping_conflicts > 0 && (
        <div data-testid="fp-conflicts" className="rounded-xl bg-[#FDE7EC] px-3.5 py-2.5">
          <p className="text-[12.5px] font-semibold text-[#C8102E]">{rd.mapping_conflicts} mapping conflict(s) need admin review</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {(rd.lists?.mapping_conflict || []).map((r) => (
              <span key={r.employee_id} className="num rounded-md bg-white/70 px-1.5 py-0.5 text-[11px] text-[#C8102E]">
                {r.nama} ({r.nip}{r.current_pin ? ` · PIN ${r.current_pin}` : ""})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Auto-Assign preview dialog */}
      <Dialog open={!!assignPv} onOpenChange={(v) => { if (!v) setAssignPv(null); }}>
        <DialogContent data-testid="fp-auto-assign-dialog" className="max-h-[85vh] gap-0 overflow-hidden rounded-[20px] border border-white/50 bg-white/80 p-0 shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-2xl sm:max-w-[720px]">
          <div className="px-5 pt-5">
            <DialogHeader className="text-left"><DialogTitle className="text-[18px] font-semibold tracking-tight">Preview Auto-Assign PIN</DialogTitle></DialogHeader>
            <p className="mt-1 text-[12.5px] text-[#8E8E93]">Only <b>READY TO ASSIGN</b> rows will be assigned. Conflicts & duplicate PINs are left unchanged.</p>
          </div>
          <div className="mt-3 max-h-[58vh] overflow-auto px-5 pb-4">
            <table className="w-full min-w-[560px] text-left text-[12.5px]">
              <thead><tr className="border-y border-black/[0.06] text-[10px] uppercase tracking-wider text-[#A1A1A6]">
                <th className="py-2 pr-2 font-semibold">Employee</th><th className="px-2 font-semibold">NIP</th>
                <th className="px-2 font-semibold">Current</th><th className="px-2 font-semibold">Derived</th><th className="px-2 font-semibold">Result</th>
              </tr></thead>
              <tbody>
                {(assignPv?.rows || []).map((r) => (
                  <tr key={r.employee_id} data-testid="fp-auto-assign-row" className="border-b border-black/[0.04]">
                    <td className="py-2 pr-2 font-medium">{r.nama}</td>
                    <td className="num px-2">{r.nip || "—"}</td>
                    <td className="num px-2">{r.current_pin || "—"}</td>
                    <td className="num px-2">{r.derived_pin || "—"}</td>
                    <td className="px-2"><span className={`rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ${RESULT_STYLE[r.result] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>{r.result.replace(/_/g, " ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 border-t border-black/[0.06] px-5 py-3">
            <span className="mr-auto self-center text-[12px] text-[#8E8E93]">{(assignPv?.counts?.READY_TO_ASSIGN || 0)} ready to assign</span>
            <button onClick={() => setAssignPv(null)} className="rounded-full px-4 py-2 text-[13px] font-medium text-[#6E6E73] hover:bg-black/[0.04]">Cancel</button>
            <button data-testid="fp-auto-assign-confirm" onClick={applyAssign} disabled={busy === "assign-apply" || !(assignPv?.counts?.READY_TO_ASSIGN)}
              className="flex items-center gap-1.5 rounded-full bg-[#0071E3] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#0077ED] disabled:opacity-50">
              {busy === "assign-apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Assign {(assignPv?.counts?.READY_TO_ASSIGN || 0)} PIN
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate source preview dialog */}
      <Dialog open={!!sourcePv} onOpenChange={(v) => { if (!v) setSourcePv(null); }}>
        <DialogContent data-testid="fp-activate-source-dialog" className="max-h-[85vh] gap-0 overflow-hidden rounded-[20px] border border-white/50 bg-white/80 p-0 shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-2xl sm:max-w-[720px]">
          <div className="px-5 pt-5">
            <DialogHeader className="text-left"><DialogTitle className="text-[18px] font-semibold tracking-tight">Activate Fingerspot for All Ready</DialogTitle></DialogHeader>
            <p className="mt-1 text-[12.5px] text-[#8E8E93]">Only ready <b>DAILY</b> employees (mapped, no conflict) switch MANUAL → FINGERSPOT. Piece-rate employees show as <b>Attendance Only</b> and never affect Hari Kerja.</p>
          </div>
          <div className="mt-3 max-h-[58vh] overflow-auto px-5 pb-4">
            <table className="w-full min-w-[560px] text-left text-[12.5px]">
              <thead><tr className="border-y border-black/[0.06] text-[10px] uppercase tracking-wider text-[#A1A1A6]">
                <th className="py-2 pr-2 font-semibold">Employee</th><th className="px-2 font-semibold">NIP</th><th className="px-2 font-semibold">PIN</th>
                <th className="px-2 font-semibold">Current</th><th className="px-2 font-semibold">New</th><th className="px-2 font-semibold">Mapping</th>
              </tr></thead>
              <tbody>
                {(sourcePv?.rows || []).map((r) => (
                  <tr key={r.employee_id} data-testid="fp-activate-source-row" className={`border-b border-black/[0.04] ${r.will_change ? "bg-[#E9F9EF]/40" : r.attendance_only ? "bg-[#FFF8F0]/60" : ""}`}>
                    <td className="py-2 pr-2 font-medium">{r.nama}</td>
                    <td className="num px-2">{r.nip || "—"}</td>
                    <td className="num px-2">{r.current_pin || "—"}</td>
                    <td className="px-2">{r.current_source}</td>
                    <td className="px-2 font-semibold">
                      {r.attendance_only ? (
                        <span data-testid="fp-attendance-only-badge" className="rounded-full bg-[#FFF1E6] px-1.5 py-0.5 text-[10.5px] font-medium text-[#B5590F]">Attendance Only — Excluded from Hari Kerja Payroll</span>
                      ) : (
                        <span style={{ color: r.will_change ? "#0E8A46" : "#8E8E93" }}>{r.new_source}</span>
                      )}
                    </td>
                    <td className="px-2"><span className={`rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ${r.mapping_status === "LINKED" ? "bg-[#E9F9EF] text-[#0E8A46]" : r.mapping_status === "CONFLICT" ? "bg-[#FDE7EC] text-[#C8102E]" : "bg-[#F1F1F4] text-[#6E6E73]"}`}>{r.mapping_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 border-t border-black/[0.06] px-5 py-3">
            <span className="mr-auto self-center text-[12px] text-[#8E8E93]">{willChange.length} will switch to FINGERSPOT</span>
            <button onClick={() => setSourcePv(null)} className="rounded-full px-4 py-2 text-[13px] font-medium text-[#6E6E73] hover:bg-black/[0.04]">Cancel</button>
            <button data-testid="fp-activate-source-confirm" onClick={applySource} disabled={busy === "src-apply" || !willChange.length}
              className="flex items-center gap-1.5 rounded-full bg-[#128C3E] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#0F7433] disabled:opacity-50">
              {busy === "src-apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} Activate {willChange.length}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
